/**
 * templates/categories/hero.js - Hero Section Templates
 * Location: /app/frontend/src/admin/page_builder/templates/categories/
 * 
 * Hero templates for prominent page headers and landing sections.
 * Uses Phase 1 primitives: layout presets, backgrounds.
 */

import { LayoutPreset } from '../../layout/presets';
import { BackgroundType, GradientDirection } from '../../layout/backgrounds';

/**
 * Hero Overlay - Full-width hero with text overlaid on background image
 * Use cases: Landing pages, campaign headers, early access signups
 */
export const hero_overlay = {
  id: 'hero_overlay',
  name: 'Hero Overlay',
  category: 'hero',
  description: 'Full-width hero with text overlaid on background image. Ideal for landing pages and campaigns.',
  thumbnail: null, // Placeholder - will use category default
  
  // Phase 1 primitives
  layout: { preset: LayoutPreset.OVERLAY },
  background: { 
    type: BackgroundType.IMAGE, 
    config: { 
      url: '', 
      position: 'center center', 
      size: 'cover' 
    },
    overlayOpacity: 40,
    overlayColor: '#000000'
  },
  cardVariant: null,
  
  // Template constraints
  allowedLayouts: [LayoutPreset.OVERLAY, LayoutPreset.SINGLE],
  allowedBackgrounds: [BackgroundType.IMAGE, BackgroundType.GRADIENT, BackgroundType.SOLID],
  maxBlocks: 1, // Only one hero per page
  
  // Default layout/background
  defaultLayout: { preset: LayoutPreset.OVERLAY },
  defaultBackground: { type: BackgroundType.IMAGE },
  
  // Block configuration (uses existing block props model)
  blockType: 'section_hero_overlay',
  defaultProps: {
    heading: 'Your Headline Here',
    subheading: 'Supporting text that explains your value proposition',
    ctaPrimaryLabel: 'Get Started',
    ctaPrimaryUrl: '#',
    ctaSecondaryLabel: 'Learn More',
    ctaSecondaryUrl: '#',
    backgroundImage: '',
    badge: '', // Optional badge text (e.g., "EARLY ACCESS")
    showBadge: false,
  },
  
  // Editable fields definition
  editableFields: [
    { key: 'heading', label: 'Heading', type: 'text' },
    { key: 'subheading', label: 'Subheading', type: 'textarea' },
    { key: 'ctaPrimaryLabel', label: 'Primary CTA Label', type: 'text' },
    { key: 'ctaPrimaryUrl', label: 'Primary CTA URL', type: 'text' },
    { key: 'ctaSecondaryLabel', label: 'Secondary CTA Label', type: 'text' },
    { key: 'ctaSecondaryUrl', label: 'Secondary CTA URL', type: 'text' },
    { key: 'backgroundImage', label: 'Background Image URL', type: 'image' },
    { key: 'badge', label: 'Badge Text', type: 'text' },
    { key: 'showBadge', label: 'Show Badge', type: 'boolean' },
  ],
};

/**
 * Hero Split - 50/50 layout with media on left, content on right
 * Use cases: Product showcases, feature highlights
 */
export const hero_split = {
  id: 'hero_split',
  name: 'Hero Split',
  category: 'hero',
  description: '50/50 split layout with media on left and content on right. Great for product showcases.',
  thumbnail: null,
  
  layout: { preset: LayoutPreset.SPLIT_50_50 },
  background: { 
    type: BackgroundType.GRADIENT,
    config: {
      direction: GradientDirection.TO_BOTTOM_RIGHT,
      from: '#18181b',
      to: '#000000'
    },
    overlayOpacity: 0
  },
  cardVariant: null,
  
  // Template constraints
  allowedLayouts: [LayoutPreset.SPLIT_50_50, LayoutPreset.SPLIT_60_40, LayoutPreset.SPLIT_40_60, LayoutPreset.MEDIA_LEFT, LayoutPreset.MEDIA_RIGHT],
  allowedBackgrounds: [BackgroundType.NONE, BackgroundType.SOLID, BackgroundType.GRADIENT],
  maxBlocks: 2, // Can have up to 2 split heroes
  
  // Default layout/background
  defaultLayout: { preset: LayoutPreset.SPLIT_50_50 },
  defaultBackground: { type: BackgroundType.GRADIENT },
  
  blockType: 'section_hero_split',
  defaultProps: {
    heading: 'Feature Headline',
    subheading: 'Describe your key feature or value proposition here.',
    ctaLabel: 'Get Started',
    ctaUrl: '#',
    mediaUrl: '',
    mediaAlt: 'Feature image',
    mediaPosition: 'left', // 'left' or 'right'
  },
  
  editableFields: [
    { key: 'heading', label: 'Heading', type: 'text' },
    { key: 'subheading', label: 'Subheading', type: 'textarea' },
    { key: 'ctaLabel', label: 'CTA Label', type: 'text' },
    { key: 'ctaUrl', label: 'CTA URL', type: 'text' },
    { key: 'mediaUrl', label: 'Media URL', type: 'image' },
    { key: 'mediaAlt', label: 'Media Alt Text', type: 'text' },
    { key: 'mediaPosition', label: 'Media Position', type: 'select', options: ['left', 'right'] },
  ],
};

/**
 * Hero Minimal - Clean text-focused hero with no media
 * Use cases: Blog headers, simple announcements, text-first pages
 */
export const hero_minimal = {
  id: 'hero_minimal',
  name: 'Hero Minimal',
  category: 'hero',
  description: 'Clean, text-focused hero with no media. Perfect for blogs and text-first pages.',
  thumbnail: null,
  
  layout: { preset: LayoutPreset.SINGLE },
  background: { 
    type: BackgroundType.SOLID,
    config: { color: 'var(--tc-colors-surface, #18181b)' },
    overlayOpacity: 0
  },
  cardVariant: null,
  
  // Template constraints
  allowedLayouts: [LayoutPreset.SINGLE],
  allowedBackgrounds: [BackgroundType.NONE, BackgroundType.SOLID, BackgroundType.GRADIENT],
  maxBlocks: 1, // Only one minimal hero per page
  
  // Default layout/background
  defaultLayout: { preset: LayoutPreset.SINGLE },
  defaultBackground: { type: BackgroundType.SOLID },
  
  blockType: 'section_hero_minimal',
  defaultProps: {
    heading: 'Page Title',
    subheading: 'Optional supporting text or tagline',
    alignment: 'center', // 'left', 'center', 'right'
    showDivider: true,
  },
  
  editableFields: [
    { key: 'heading', label: 'Heading', type: 'text' },
    { key: 'subheading', label: 'Subheading', type: 'textarea' },
    { key: 'alignment', label: 'Text Alignment', type: 'select', options: ['left', 'center', 'right'] },
    { key: 'showDivider', label: 'Show Divider', type: 'boolean' },
  ],
};

export default {
  hero_overlay,
  hero_split,
  hero_minimal,
};
